import { useEffect, useState } from 'react'
import { getUsers } from './crmApi'
import { USERS } from './crmData'

/**
 * The CRM's users ARE the Support agents (same accounts, same logins). This hook is the single
 * source for that list everywhere in the CRM UI, so owner names, filters, analytics and the team
 * pickers all resolve to the same real people.
 *
 * - Fetches `/crm/users` (the real `agents` table) once and caches it module-wide, so N components
 *   don't each hit the endpoint.
 * - Falls back to the mock USERS fixture when the API is unreachable or empty (dev / pre-deploy),
 *   matching the graceful-degradation the rest of the CRM uses — nothing breaks offline.
 */
export interface CrmAgent { id: string; name: string; email?: string; role?: string }

let cache: CrmAgent[] | null = null
let inflight: Promise<CrmAgent[]> | null = null

function load(): Promise<CrmAgent[]> {
  if (cache) return Promise.resolve(cache)
  if (!inflight) {
    inflight = getUsers()
      .then((u: any[]) => {
        cache = Array.isArray(u) && u.length ? (u as CrmAgent[]) : (USERS as CrmAgent[])
        return cache
      })
      .catch(() => USERS as CrmAgent[])
  }
  return inflight
}

export function useAgents() {
  const [agents, setAgents] = useState<CrmAgent[]>(cache || (USERS as CrmAgent[]))
  useEffect(() => {
    let alive = true
    load().then((a) => { if (alive) setAgents(a) })
    return () => { alive = false }
  }, [])
  const agentById = (id?: string | null): CrmAgent | undefined => (id ? agents.find((a) => a.id === id) : undefined)
  const agentName = (id?: string | null): string => agentById(id)?.name || ''
  return { agents, agentById, agentName }
}
